import { useMemo, useState, type KeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";
import { ConsoleShell, PageHead } from "../components/ConsoleShell.js";
import { Card } from "../components/Card.js";
import { Tag, Btn, Banner, cautionVariant } from "../components/index.js";
import { Frame } from "../components/Frame.js";
import { MarkdownText } from "../components/MarkdownText.js";
import { LoadingState, ErrorState, EmptyState } from "../components/EmptyState.js";
import { Icon } from "../components/Icon.js";
import { useAsync } from "../lib/useAsync.js";
import { manuals, books as booksApi, mediaUrl } from "../lib/api.js";
import { bboxToFrame } from "../lib/bbox.js";
import { mmss, type StepWithCandidates } from "../lib/demo.js";
import type { Book, Manual } from "@tebiki/shared";

/** Screen H — review queue. Real data: lists manuals in `review` status and publishes on approve. */
export function Approval() {
  const navigate = useNavigate();
  const { data, loading, error, offline, reload } = useAsync<Manual[]>(
    () => manuals.list().then((r) => r.items.filter((m) => m.status === "review")),
  );
  const queue = data ?? [];
  const [activeId, setActiveId] = useState<string | null>(null);
  const active = useMemo(
    () => queue.find((m) => m.manualId === activeId) ?? queue[0] ?? null,
    [queue, activeId],
  );

  const [approving, setApproving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  // Full detail (steps) of the selected manual — the real review content.
  const detail = useAsync<Manual | null>(
    () => (active ? manuals.get(active.manualId) : Promise.resolve(null)),
    [active?.manualId ?? ""],
  );
  const steps = (detail.data?.steps ?? []) as StepWithCandidates[];

  // 承認後の配信先が分かるように: このマニュアルが収まっている本を表示する。
  const bookList = useAsync<Book[]>(() => booksApi.list().catch(() => [] as Book[]), []);
  const containingBooks = useMemo(
    () => (active ? (bookList.data ?? []).filter((b) => b.manualIds.includes(active.manualId)) : []),
    [bookList.data, active],
  );

  async function approve() {
    if (!active) return;
    setApproving(true);
    setNotice(null);
    try {
      await manuals.approve(active.manualId);
      setNotice(`「${active.title}」を公開しました。`);
      setActiveId(null);
      await reload();
    } catch {
      setNotice("公開に失敗しました。権限（owner/admin/reviewer）または通信を確認してください。");
    } finally {
      setApproving(false);
    }
  }

  function onRowKey(e: KeyboardEvent<HTMLButtonElement>, id: string) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setActiveId(id);
    }
  }

  return (
    <ConsoleShell
      breadcrumb={["TEBIKI", "承認待ち"]}
      actions={
        <span className="statuspill">
          <span className="sdot sdot--hi" aria-hidden="true" />
          {queue.length} 件待ち
        </span>
      }
    >
      <PageHead title={<>承認<span className="hl">待ち</span></>}>
        公開前の最終確認。内容を見て、問題なければ公開します。承認すると現場に配信されます。
      </PageHead>

      {notice ? (
        <Banner variant={notice.includes("失敗") ? "warn" : "ai"} icon={notice.includes("失敗") ? <Icon name="warning" /> : <Icon name="check" />} className="reveal">
          <span className="grow">{notice}</span>
        </Banner>
      ) : null}

      {loading ? (
        <LoadingState label="承認待ちを読み込み中…" />
      ) : error ? (
        <ErrorState message={error} offline={offline} onRetry={reload} />
      ) : queue.length === 0 ? (
        <EmptyState icon={<Icon name="approval" size={30} />} title="承認待ちはありません">
          すべてのマニュアルが処理済みです。新しい申請が来るとここに表示されます。
        </EmptyState>
      ) : (
        <div
          className="appr-grid"
          style={{ display: "grid", gridTemplateColumns: "minmax(0,360px) 1.4fr", gap: 18, alignItems: "start" }}
        >
          {/* ---- left: real review queue ---- */}
          <Card crop className="reveal" style={{ overflow: "hidden" }}>
            {queue.map((m, i) => {
              const on = active?.manualId === m.manualId;
              const n = Array.isArray(m.steps) ? m.steps.length : 0;
              return (
                <button
                  key={m.manualId}
                  type="button"
                  aria-current={on}
                  onClick={() => setActiveId(m.manualId)}
                  onKeyDown={(e) => onRowKey(e, m.manualId)}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "52px 1fr",
                    gap: 12,
                    width: "100%",
                    textAlign: "left",
                    alignItems: "center",
                    padding: "12px 14px",
                    border: 0,
                    borderBottom: i < queue.length - 1 ? "1px solid var(--line)" : undefined,
                    borderLeft: `3px solid ${on ? "var(--hi)" : "transparent"}`,
                    background: on ? "color-mix(in srgb,var(--ink) 5%,transparent)" : "transparent",
                    cursor: "pointer",
                  }}
                >
                  <Frame paper className="thumb" style={{ width: 52, aspectRatio: "1" }} src={mediaUrl(m.thumbnailUrl)} subject />
                  <div style={{ minWidth: 0 }}>
                    <div className="display jp" style={{ fontSize: 16, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {m.title}
                    </div>
                    <div className="row" style={{ marginTop: 3, gap: 8 }}>
                      <Tag mono>v{m.version ?? 1}</Tag>
                      <Tag mono>
                        {m.sourceType === "video" ? "動画" : "画像"}
                        {n > 0 ? ` · ${n}手順` : ""}
                      </Tag>
                    </div>
                  </div>
                </button>
              );
            })}
          </Card>

          {/* ---- right: selected manual — real steps preview ---- */}
          <Card crop className="reveal d1" style={{ overflow: "hidden" }}>
            {active ? (
              <>
                <div
                  className="spread"
                  style={{ padding: "18px 20px", borderBottom: "1px solid var(--line)", flexWrap: "wrap", gap: 10 }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div className="kicker">REVIEW · v{active.version ?? 1}</div>
                    <h2 className="display jp" style={{ fontSize: "var(--step-2)", marginTop: 6 }}>
                      {active.title}
                    </h2>
                    <div className="row" style={{ marginTop: 8, gap: 8, flexWrap: "wrap" }}>
                      <Tag>{active.meta?.category ?? "未分類"}</Tag>
                      <Tag mono>{steps.length} STEPS</Tag>
                      {containingBooks.map((b) => (
                        <Tag key={b.bookId} dot color="var(--info-ink)">
                          本: {b.title}
                        </Tag>
                      ))}
                    </div>
                    {!bookList.loading && containingBooks.length === 0 ? (
                      <p className="jp" style={{ margin: "8px 0 0", fontSize: 12.5, color: "var(--warn-ink)" }}>
                        どの本にも入っていません — 公開後は書架の未整理トレイに並びます（編集プレビューの「提出する」から本に入れられます）。
                      </p>
                    ) : null}
                  </div>
                  <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
                    <Btn variant="ghost" style={{ whiteSpace: "nowrap" }} onClick={() => navigate(`/preview/${active.manualId}`, { state: { from: "/approval" } })}>
                      編集プレビュー
                    </Btn>
                    <Btn variant="primary" icon={<Icon name="approval" />} style={{ whiteSpace: "nowrap" }} onClick={approve} disabled={approving}>
                      {approving ? "公開中…" : "承認して公開"}
                    </Btn>
                  </div>
                </div>

                {detail.loading ? (
                  <LoadingState label="手順を読み込み中…" />
                ) : detail.error ? (
                  <ErrorState message={detail.error} offline={detail.offline} onRetry={detail.reload} />
                ) : steps.length === 0 ? (
                  <EmptyState icon={<Icon name="studio" size={30} />} title="手順がまだありません">
                    このマニュアルには確認できる手順がありません。
                  </EmptyState>
                ) : (
                  <div>
                    {steps.map((step, i) => (
                      /* .appr-step: layout lives in tebiki.css so the ≤560px rescue rules
                         (smaller gap/padding + 84px frame) actually apply */
                      <article
                        key={step.stepId}
                        className="appr-step"
                        style={{
                          borderBottom: i < steps.length - 1 ? "1px dashed var(--line)" : undefined,
                        }}
                      >
                        <Frame
                          paper
                          src={mediaUrl(step.imageUrl)}
                          cap={step.videoTimecodeMs != null ? mmss(step.videoTimecodeMs) : undefined}
                          bbox={bboxToFrame(step.bbox, step.objectLabel)}
                          style={{ width: "clamp(84px, 24vw, 104px)", aspectRatio: "3/4" }}
                        />
                        <div style={{ minWidth: 0 }}>
                          <Tag mono>STEP {String(step.order).padStart(2, "0")}</Tag>
                          <h3 className="jp t-lg" style={{ margin: "8px 0 4px" }}>
                            {step.title}
                          </h3>
                          <MarkdownText
                            className="jp"
                            style={{ margin: 0, color: "var(--muted)", fontSize: 14 }}
                            text={step.caption}
                            terms={step.highlights?.terms}
                          />
                          {step.cautions.map((c, ci) => (
                            <Banner key={ci} variant={cautionVariant(c.level)}>
                              <span className="jp">{c.text}</span>
                            </Banner>
                          ))}
                        </div>
                      </article>
                    ))}
                  </div>
                )}
              </>
            ) : null}
          </Card>
        </div>
      )}
    </ConsoleShell>
  );
}
